import { EmptyState } from '@today/ui-components'
import type { ReactElement } from 'react'
import type { AssignmentView, Translate } from './customer-types'

/** Label/value rows of the Profile tab (organism). Values arrive already formatted. */
export function CustomerProfileView({ t, rows }: { t: Translate; rows: Array<[string, string]> }): ReactElement {
	return (
		<dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-ds-base">
			{rows.map(([labelKey, value]) => (
				<div key={labelKey} className="contents">
					<dt className="text-ds-text-muted">{t(labelKey)}</dt>
					<dd className="text-ds-text">{value}</dd>
				</div>
			))}
		</dl>
	)
}

/** Current assignment + history timeline, newest first (organism). */
export function AssignmentTimelineView({ t, history }: { t: Translate; history: AssignmentView[] }): ReactElement {
	const [current] = history
	return (
		<section className="flex flex-col gap-3">
			<h2 className="text-ds-base font-semibold text-ds-text">{t('assignment.current')}</h2>
			<p className="text-ds-text">{current ? `${current.orgUnitName} · ${current.userName ?? '—'}` : '—'}</p>
			<h2 className="text-ds-base font-semibold text-ds-text">{t('assignment.history')}</h2>
			<ol className="flex flex-col gap-2">
				{history.map((row) => (
					<li key={row.id} className="rounded-ds-md border border-ds-border p-3 text-ds-sm">
						<p className="text-ds-text">{`${row.orgUnitName} · ${row.userName ?? '—'}`}</p>
						<p className="text-ds-text-muted">
							{`${t(`assignment.reasons.${row.reason}`)} · ${t('assignment.since')} ${row.from}`}
							{row.to ? ` · ${t('assignment.until')} ${row.to}` : ''}
						</p>
					</li>
				))}
			</ol>
		</section>
	)
}

/** Read-only partner attributes (organism). */
export function AttributesTableView({
	t,
	attributes,
}: {
	t: Translate
	attributes: Array<{ key: string; value: string; source: string }>
}): ReactElement {
	if (attributes.length === 0) return <EmptyState title={t('attributes.empty')} />
	return (
		<table className="w-full text-ds-sm">
			<thead>
				<tr className="text-left text-ds-text-muted">
					<th scope="col">{t('attributes.key')}</th>
					<th scope="col">{t('attributes.value')}</th>
					<th scope="col">{t('attributes.source')}</th>
				</tr>
			</thead>
			<tbody>
				{attributes.map((row) => (
					<tr key={row.key}>
						<td>{row.key}</td>
						<td>{row.value}</td>
						<td>{t(`source.${row.source}`)}</td>
					</tr>
				))}
			</tbody>
		</table>
	)
}

/** The customer's audit events, one row per event (organism). */
export function ActivityListView({
	t,
	events,
}: {
	t: Translate
	events: Array<{ id: string; label: string; when: string }>
}): ReactElement {
	if (events.length === 0) return <EmptyState title={t('activity.empty')} />
	return (
		<ul className="flex flex-col gap-1 text-ds-sm">
			{events.map((event) => (
				<li key={event.id} className="flex justify-between border-b border-ds-border py-1">
					<span className="text-ds-text">{event.label}</span>
					<span className="text-ds-text-muted">{event.when}</span>
				</li>
			))}
		</ul>
	)
}
